import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withFieldProps } from 'hooks/useFieldProps';
import { withStyles } from 'theme/withStyles';
import FlyupLabel from 'common/FlyupLabel';
import Spinner from 'common/Spinner';
import ErrorMsg from 'common/ErrorMsg';
import Help from 'common/Help';
import Tooltip from 'common/Tooltip';

import rootStyles from './styles/root';
import inputStyles from './styles/input';

const styles = ({ forms, palette, spacing, typography }) => ({
  hiddenInput: {
    ...inputStyles({ forms, palette, typography }),
    background: 'none',
    border: 'none',
    fontFamily: 'Inconsolata, monospace',
    left: 0,
    marginTop: 0,
    pointerEvents: 'none',
    position: 'absolute',
    top: 0,
  },
  input: {
    ...inputStyles({ forms, palette, typography }),
    '&::selection': {
      backgroundColor: '#B3D7FF',
      color: 'transparent',
    },
    caretColor: palette.text,
    color: 'transparent',
    fontFamily: 'Inconsolata, monospace',
  },
  root: rootStyles({ spacing }),
});

const getVariableFormatting = (val, secretFormat) => {
  const placeholders = [];
  let curFormatChar;
  let nextFormatChar;
  let valPos = 0;
  let lookbackVal;

  // Split at first @ only
  const aryFormat = secretFormat.split(/@(.+)/);
  const aryVal = val.split(/@(.+)/);

  for (let i = 0; i < aryFormat.length; i += 1) {
    if (aryVal[i]) {
      // Mask all characters as default
      placeholders[i] = Array(aryVal[i].length + 1).join('*');
      placeholders[i] = placeholders[i].split('');
      valPos = 0;

      // Unmask specific characters
      for (let j = 0; j < aryFormat[i].length; j += 1) {
        curFormatChar = aryFormat[i][j];
        nextFormatChar = aryFormat[i][j + 1];

        if (curFormatChar === 'C' || curFormatChar === '.') {
          while (valPos < placeholders[i].length) {
            placeholders[i][valPos] = aryVal[i].charAt(valPos);
            valPos += 1;
            break;
          }
        } else if (curFormatChar === 'A') {
          while (valPos < placeholders[i].length) {
            if (nextFormatChar === '.' && aryVal[i].charAt(valPos) === '.') {
              placeholders[i][valPos] = aryVal[i].charAt(valPos);
              break;
            } else {
              placeholders[i][valPos] = aryVal[i].charAt(valPos);
            }

            valPos += 1;
          }
        } else if (curFormatChar === '*') {
          while (valPos < placeholders[i].length) {
            if (nextFormatChar === '.' && aryVal[i].charAt(valPos) === '.') {
              placeholders[i][valPos] = aryVal[i].charAt(valPos);
              break;
            }

            valPos += 1;
          }
        }
      }

      // Single lookback (***C)
      if (aryFormat[i][aryFormat[i].length - 1] === 'C') {
        lookbackVal = aryVal[i].charAt(aryVal[i].length - 1);
        placeholders[i][placeholders[i].length - 1] = lookbackVal;
      }

      // Double lookback (***CC)
      if (
        aryFormat[i][aryFormat[i].length - 1] === 'C' &&
        aryFormat[i][aryFormat[i].length - 2] === 'C'
      ) {
        lookbackVal = aryVal[i].charAt(aryVal[i].length - 2);
        placeholders[i][placeholders[i].length - 2] = lookbackVal;
      }

      // Triple lookback (***CCC)
      if (
        aryFormat[i][aryFormat[i].length - 1] === 'C' &&
        aryFormat[i][aryFormat[i].length - 2] === 'C' &&
        aryFormat[i][aryFormat[i].length - 3] === 'C'
      ) {
        lookbackVal = aryVal[i].charAt(aryVal[i].length - 3);
        placeholders[i][placeholders[i].length - 3] = lookbackVal;
      }

      placeholders[i] = placeholders[i].join('');
    }
  }

  return placeholders.join('@');
};

const MaskedSecretVariableString = ({
  classes,
  error,
  fieldDomId,
  format,
  help,
  inputmode,
  loading,
  name,
  onBlur,
  onChange,
  onDisable,
  onFocus,
  onPaste,
  placeholder,
  readOnly,
  required,
  tooltip,
  type,
  validate,
  value,
}) => {
  let maskedValue = '';
  let unMaskedValue = '';
  if (value) {
    maskedValue = getVariableFormatting(value, format);
    unMaskedValue = value;
  }
  return (
    <>
      <div className={classnames('form-group', { error }, classes.root)}>
        <Spinner loading={loading} />
        <FlyupLabel
          fieldName={name}
          error={error}
          value={value}
          htmlFor={fieldDomId}
          required={required}
          tooltip={tooltip}
        />
        <input
          type={type}
          inputMode={inputmode}
          className={classnames('form-control', 'apply-secret-mask', classes.input)}
          aria-invalid={error}
          name={name}
          id={fieldDomId}
          value={unMaskedValue}
          placeholder={placeholder}
          readOnly={readOnly}
          required={required}
          onChange={(e) => {
            onChange(name, e?.target?.value);
          }}
          onFocus={onFocus}
          onBlur={() => {
            onBlur();
            validate();
          }}
          onPaste={onPaste}
          autoComplete="off"
          onCut={onDisable}
          onCopy={onDisable}
          onContextMenu={onDisable}
        />
        <input
          value={maskedValue}
          type="text"
          onChange={() => {}}
          className={classnames('secret-format', classes.hiddenInput)}
          autoComplete="off"
          tabIndex="-1"
        />
        <Tooltip tooltip={tooltip} labeledBy={`label-${fieldDomId}`} />
        <Help help={help} />
      </div>

      <ErrorMsg name={name} />
    </>
  );
};

MaskedSecretVariableString.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  error: PropTypes.bool.isRequired,
  fieldDomId: PropTypes.string.isRequired,
  format: PropTypes.string.isRequired,
  help: PropTypes.string.isRequired,
  inputmode: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onDisable: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  onPaste: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  readOnly: PropTypes.bool.isRequired,
  required: PropTypes.bool.isRequired,
  tooltip: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  validate: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
};

const MaskedSecretVariableStringStyled = withStyles(styles)(MaskedSecretVariableString);

export default withFieldProps()(MaskedSecretVariableStringStyled);
